import { ArrowRightOutlined } from '@ant-design/icons';
import { Avatar, Col, List, Row, Typography } from 'antd';
import { t } from 'helpers/i18n';
import { INotification } from 'interfaces';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { parseNotificationDeeplink } from './utils';
import { dateTimeHelpers } from 'helpers';
import dayjs from 'dayjs';
import { dateTimeConstants, trackingConstants } from 'constants/index';

const { formatDateTime } = dateTimeHelpers;

const { DATE_TIME_SLASH_SHORT_FORMAT } = dateTimeConstants;
const { TRACKING_CONTENT_NAME } = trackingConstants;

interface NotificationItemProps {
  notification: INotification;
  onMarkRead: (notification: INotification) => void;
  // Called after navigating to an internal route (e.g. to close the modal).
  onAfterNavigate?: () => void;
  // Tracking region the item is rendered in (bell popover vs modal).
  trackingRegionName?: string;
}
const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkRead,
  onAfterNavigate,
  trackingRegionName,
}) => {
  const history = useHistory();
  const deeplink = parseNotificationDeeplink(notification);

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkRead(notification);
    if (!deeplink) return;
    if (deeplink.startsWith('http')) {
      window.open(deeplink, '_blank');
    } else {
      history.push(deeplink);
      onAfterNavigate?.();
    }
  };

  return (
    <List.Item.Meta
      avatar={
        <Avatar
          shape="square"
          size="large"
          style={{
            backgroundColor: '#d9d9d9',
          }}
          src={notification.image_url}
        />
      }
      title={
        <Row justify="space-between" wrap={false}>
          <Col flex="auto">
            <Typography.Text strong ellipsis className="w-100">
              {notification.title}
            </Typography.Text>
          </Col>
          <Col flex="none" className="ml-half">
            <Typography.Text type="secondary" className="date-time">
              {formatDateTime(
                dayjs(notification.created_at),
                DATE_TIME_SLASH_SHORT_FORMAT
              )}
            </Typography.Text>
          </Col>
        </Row>
      }
      description={
        <>
          <Typography.Paragraph
            type="secondary"
            ellipsis={{ rows: 2 }}
            style={{ marginBottom: deeplink ? 4 : 0 }}
          >
            {notification.message}
          </Typography.Paragraph>
          {deeplink && (
            <Typography.Link
              data-testid="notification-item-action"
              data-track-content
              data-content-region-name={trackingRegionName}
              data-content-name={TRACKING_CONTENT_NAME.NotificationDeeplink}
              data-content-payload={JSON.stringify({
                id: notification.id,
                deeplink,
                type: deeplink?.startsWith('http') ? 'external' : 'internal',
              })}
              onClick={handleActionClick}
            >
              {t('Notifications.CheckAndRequest')} <ArrowRightOutlined />
            </Typography.Link>
          )}
        </>
      }
    />
  );
};

export default NotificationItem;
